/**
 * AtlasOriginRebaseModule — FP64 origin rebasing for the 2048 km shard
 * (terrain reference O.8), implemented at the ONE choke point that requires
 * zero changes anywhere else: the scene's own transform.
 *
 * Why this works: every module keeps writing ABSOLUTE world coordinates into
 * scene children (chunks, settlements, parts, NPCs, the avatar rig — server
 * coords stay authoritative and untouched). Three.js composes each child's
 * matrixWorld = sceneMatrix × childMatrix in JS DOUBLES on the CPU; by holding
 * scene.position at minus-the-current-origin, the huge translations cancel in
 * f64 BEFORE the f32 GPU upload, so vertices, shadows, and culling all see
 * small camera-relative numbers. Without this, positions beyond ~100 km lose
 * float32 precision in the shader (≈6 cm ulp at 1,000 km → visible jitter).
 *
 * The origin re-quantizes to integer metres whenever the avatar drifts >10 km
 * from it — a rare, single-frame reassignment. Physics (Rapier) runs in
 * absolute space independent of rendering and is unaffected; raycasts and the
 * VR panel use matrixWorld and stay consistent inside the shifted frame. Known
 * cosmetic nit: shader macro noise sampled from positionWorld shifts with the
 * origin, so the large-scale brightness pattern re-anchors on rebase (subtle,
 * ~once per 10 km travelled).
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { setRenderAnchor } from '../lib/local_origin.js';

const REBASE_THRESHOLD_M = 10_000;

export class AtlasOriginRebaseModule implements IModule {
  readonly name = 'AtlasOriginRebase';

  private ox = 0;
  private oz = 0;

  init(app: App): void {
    const scene = app.world.scene;
    app.world.addSystem(makeSystem('AtlasOriginRebase', Phase.FixedSim, -5, () => {
      const dx = atlas.localX - this.ox;
      const dz = atlas.localZ - this.oz;
      if (dx * dx + dz * dz <= REBASE_THRESHOLD_M * REBASE_THRESHOLD_M) return;
      // Quantize to integer metres so repeated rebases accumulate zero drift.
      this.ox = Math.round(atlas.localX);
      this.oz = Math.round(atlas.localZ);
      scene.position.set(-this.ox, 0, -this.oz);
      scene.updateMatrixWorld(true);
      // Instanced pools store matrices relative to this anchor (float32 attributes
      // can't ride the scene rebase — lib/local_origin.ts).
      setRenderAnchor(this.ox, this.oz);
    }));
  }
}
